var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, observable, reaction } from "mobx";
import { AddJoinCommonInformedConsentBaseViewModel } from "./AddJoinCommonInformedConsentBaseViewModel";
import { SimpleDialogModel } from "../../SimpleDialogModel";
import { NOT_LOADED } from "../../../Infra/RemoteData";
import { StudyTranslationLanguage, StudySectionType, StudySectionImageStatus, } from "healthex-common/study.dtos";
import { FormModel } from "../../FormModel";
import { isKeyOfObject, willNeverHappen, withoutUndefinedProperties } from "healthex-common/utils";
import { TranscodeInput } from "healthex-common/transcode.dtos";
import { isErr } from "healthex-common/result";
import { languageDescription } from "../../LanguageDescriptions";
import { isBefore } from "date-fns";
import { ProjectPageName, ProjectSectionName, projectSectionTitle, ProjectStaticPageName, } from "healthex-common/project.data.dtos";
import { downloadEnglishInformedConsentTranslationsAsCSV, getTranslatableFieldsFromProjectSections, translationInputReference, } from "../../ProjectTranslationService";
export class AddJoinCommonInformedConsentTranslationsViewModel extends AddJoinCommonInformedConsentBaseViewModel {
    constructor(opts) {
        super(opts);
        this.creatingTranslationImages = false;
        this.translationsConfirmCloseDialog = new SimpleDialogModel();
        this.downloadCompleteDialog = new SimpleDialogModel();
        this.manageTranslationsDialog = new SimpleDialogModel();
        this.confirmRetranslationDialog = new SimpleDialogModel();
        this.welcomeBackToTranslationsDialog = new SimpleDialogModel();
        this.editEnglishTranslationsDialog = new SimpleDialogModel();
        this.translationsAddedDialog = new SimpleDialogModel();
        this.enrollmentVideoTranslationUploadingInfo = null;
        this.autoSaveStudyInput = {};
        this.studyTranslationFormModel = new FormModel({
            ...translationInputReference,
        }, [], (input) => this.autoSaveSelectedLanguage(input));
        this.manageTranslationFormModel = new FormModel({
            studyTranslatableFields: [],
        }, [], (input) => this.autoSaveLanguages(input));
        this.translationWizardImportType = "FULL";
        this.showDownloadDialogBackButton = false;
        this.showDownloadDialogImportButton = false;
        this.sectionsGeneratingAI = new Set();
        this.sectionsGeneratingAIErrors = new Set();
        this.nullIfEmpty = (value) => (value === undefined ? undefined : value === "" ? null : value);
        this.informedConsentTranslationsWizardUIManager = opts.container.informedConsentProjectTranslationsWizardUIManager;
        this.studyService = opts.container.studyService;
        this.authManager = opts.container.authManager;
        this.persistence = opts.container.persistence;
        this.provisioningService = opts.container.provisioningService;
        this.studySectionImageService = opts.container.studySectionImageService;
        this.studyManager = opts.container.studyManager;
    }
    run() {
        super.run();
        this.disposers.push(reaction(() => [this.study], () => {
            var _a;
            if (this.study === null || this.study === NOT_LOADED)
                return;
            this.updateManageTranslationFormInput((_a = this.study.studyTranslatableFields) === null || _a === void 0 ? void 0 : _a.map((field) => {
                var _a;
                return ({
                    language: field.language,
                    enabled: (_a = field.enabled) !== null && _a !== void 0 ? _a : false,
                });
            }));
        }, { fireImmediately: true }), reaction(() => [this.selectedLanguageSectionTranslationProgressInfo], async () => {
            var _a;
            if (!this.selectedLanguageSectionTranslationProgressInfo)
                return;
            if (this.selectedTranslationLanguage !== "COMING_SOON") {
                await this.ensureTranslationImagesExist(this.selectedTranslationLanguage);
            }
            const newInput = this.getInputFromSelectedLanguageSectionTranslationProgressInfo(this.selectedLanguageSectionTranslationProgressInfo);
            const { enrollmentVideo: autoSaveEnrollmentVideo, ...autoSaveWithoutVideos } = this.autoSaveStudyInput;
            const isEnrollmentVideoBeingDeleted = this.studyTranslationFormModel.input.enrollmentVideo === null &&
                this.studyTranslationFormModel.input.deleteEnrollmentTranscode === true;
            const inputToMerge = { ...newInput };
            if (isEnrollmentVideoBeingDeleted) {
                delete inputToMerge.enrollmentVideo;
            }
            this.studyTranslationFormModel.input = {
                ...this.studyTranslationFormModel.input,
                ...inputToMerge,
                ...autoSaveWithoutVideos,
                additionalSections: (_a = newInput.additionalSections) !== null && _a !== void 0 ? _a : [],
            };
        }, { fireImmediately: true }), reaction(() => [this.addProjectUIManager.hasTranslations], () => {
            if (this.addProjectUIManager.hasTranslations &&
                !this.addProjectUIManager.translationsAddedThisSession &&
                !this.addProjectUIManager.didDownloadTranslationsThisSession &&
                this.study &&
                this.study !== NOT_LOADED &&
                this.persistence.getEnglishStringsDownloaded(this.study.id)) {
                this.persistence.removeEnglishStringsDownloaded(this.study.id);
                this.welcomeBackToTranslationsDialog.open = true;
            }
        }, { fireImmediately: true }), reaction(() => this.addProjectUIManager.translationsManuallyCompleted, async (value) => {
            if (value)
                this.translationsAddedDialog.open = true;
        }), reaction(() => this.addProjectUIManager.conflictedStudyInput, () => {
            if (this.addProjectUIManager.conflictedStudyInput.studyTranslatableFields) {
                Object.keys(this.addProjectUIManager.conflictedStudyInput.studyTranslatableFields).forEach((key) => delete this.autoSaveStudyInput[key]);
            }
        }));
    }
    stop() {
        super.stop();
        this.disposers.forEach((disposer) => disposer());
        this.disposers = [];
    }
    getInputFromSelectedLanguageSectionTranslationProgressInfo(info) {
        const { translationInput } = info;
        const formatStringArray = (value, defaultArraySize) => {
            if (value === undefined)
                return undefined;
            return !value || value.length === 0 ? Array(defaultArraySize).fill("") : value;
        };
        const defaultEmptyString = (value) => {
            if (value === undefined)
                return undefined;
            return value !== null && value !== void 0 ? value : "";
        };
        const deepClone = (value) => {
            if (value === undefined)
                return undefined;
            return JSON.parse(JSON.stringify(value));
        };
        const result = {
            ...withoutUndefinedProperties({
                loginTitle: defaultEmptyString(translationInput.loginTitle),
                loginText: defaultEmptyString(translationInput.loginText),
                welcomeTitle: defaultEmptyString(translationInput.welcomeTitle),
                welcomeText: defaultEmptyString(translationInput.welcomeText),
                dataUpdateWelcomeTitle: defaultEmptyString(translationInput.dataUpdateWelcomeTitle),
                dataUpdateWelcomeText: defaultEmptyString(translationInput.dataUpdateWelcomeText),
                californiaBillOfRightsTitle: defaultEmptyString(translationInput.californiaBillOfRightsTitle),
                californiaBillOfRightsText: defaultEmptyString(translationInput.californiaBillOfRightsText),
                studyInformationTitle: defaultEmptyString(translationInput.studyInformationTitle),
                purposeOfResearchTitle: defaultEmptyString(translationInput.purposeOfResearchTitle),
                lengthOfStudyNumberOfParticipantsTitle: defaultEmptyString(translationInput.lengthOfStudyNumberOfParticipantsTitle),
                participantTitle: defaultEmptyString(translationInput.participantTitle),
                participantInstructionsForGuidedConsent: defaultEmptyString(translationInput.participantInstructionsForGuidedConsent),
                informedConsentStatement: defaultEmptyString(translationInput.informedConsentStatement),
                lengthOfStudyNumberOfParticipantsText: defaultEmptyString(translationInput.lengthOfStudyNumberOfParticipantsText),
                healthRecordUpdatesTitle: defaultEmptyString(translationInput.healthRecordUpdatesTitle),
                healthRecordUpdatesText: defaultEmptyString(translationInput.healthRecordUpdatesText),
                proceduresAndInterventionsTitle: defaultEmptyString(translationInput.proceduresAndInterventionsTitle),
                timelineTitle: defaultEmptyString(translationInput.timelineTitle),
                benefitsTitle: defaultEmptyString(translationInput.benefitsTitle),
                risksTitle: defaultEmptyString(translationInput.risksTitle),
                voluntaryParticipationTitle: defaultEmptyString(translationInput.voluntaryParticipationTitle),
                voluntaryParticipationText: defaultEmptyString(translationInput.voluntaryParticipationText),
                withdrawalImplicationsTitle: defaultEmptyString(translationInput.withdrawalImplicationsTitle),
                injuryProtocolTitle: defaultEmptyString(translationInput.injuryProtocolTitle),
                participationTerminationTitle: defaultEmptyString(translationInput.participationTerminationTitle),
                dataTypesTitle: defaultEmptyString(translationInput.dataTypesTitle),
                sensitiveDataText: defaultEmptyString(translationInput.sensitiveDataText),
                futureUseOfDataTitle: defaultEmptyString(translationInput.futureUseOfDataTitle),
                confidentialityMeasuresTitle: defaultEmptyString(translationInput.confidentialityMeasuresTitle),
                compensationTitle: defaultEmptyString(translationInput.compensationTitle),
                costTitle: defaultEmptyString(translationInput.costTitle),
                researchResultsTitle: defaultEmptyString(translationInput.researchResultsTitle),
                whoToContactTitle: defaultEmptyString(translationInput.whoToContactTitle),
                whoToContactText: defaultEmptyString(translationInput.whoToContactText),
                informedConsentTitle: defaultEmptyString(translationInput.informedConsentTitle),
                informedConsentText: defaultEmptyString(translationInput.informedConsentText),
                informedConsentYesText: defaultEmptyString(translationInput.informedConsentYesText),
                informedConsentNoText: defaultEmptyString(translationInput.informedConsentNoText),
                informedConsentInterestedText: defaultEmptyString(translationInput.informedConsentInterestedText),
                hipaaTitle: defaultEmptyString(translationInput.hipaaTitle),
                hipaaText: defaultEmptyString(translationInput.hipaaText),
                thankYouConsentedTitle: defaultEmptyString(translationInput.thankYouConsentedTitle),
                thankYouConsentedText: defaultEmptyString(translationInput.thankYouConsentedText),
                dataUpdateThankYouConsentedTitle: defaultEmptyString(translationInput.dataUpdateThankYouConsentedTitle),
                dataUpdateThankYouConsentedText: defaultEmptyString(translationInput.dataUpdateThankYouConsentedText),
                thankYouDeclinedTitle: defaultEmptyString(translationInput.thankYouDeclinedTitle),
                thankYouDeclinedText: defaultEmptyString(translationInput.thankYouDeclinedText),
                thankYouImInterestedTitle: defaultEmptyString(translationInput.thankYouImInterestedTitle),
                thankYouImInterestedText: defaultEmptyString(translationInput.thankYouImInterestedText),
                parentGuardianTitle: defaultEmptyString(translationInput.parentGuardianTitle),
                parentGuardianInstructions: defaultEmptyString(translationInput.parentGuardianInstructions),
                parentGuardianSignatureTitle: defaultEmptyString(translationInput.parentGuardianSignatureTitle),
                parentGuardianInformedConsentStatement: defaultEmptyString(translationInput.parentGuardianInformedConsentStatement),
                additionalParentGuardianTitle: defaultEmptyString(translationInput.additionalParentGuardianTitle),
                additionalParentGuardianInstructions: defaultEmptyString(translationInput.additionalParentGuardianInstructions),
                additionalParentGuardianExemptSignatureText: defaultEmptyString(translationInput.additionalParentGuardianExemptSignatureText),
                additionalParentGuardianSignatureTitle: defaultEmptyString(translationInput.additionalParentGuardianSignatureTitle),
                additionalParentGuardianInformedConsentStatement: defaultEmptyString(translationInput.additionalParentGuardianInformedConsentStatement),
                minorParticipantTitle: defaultEmptyString(translationInput.minorParticipantTitle),
                minorParticipantInstructions: defaultEmptyString(translationInput.minorParticipantInstructions),
                minorParticipantExemptFromAssentText: defaultEmptyString(translationInput.minorParticipantExemptFromAssentText),
                minorParticipantAssentTitle: defaultEmptyString(translationInput.minorParticipantAssentTitle),
                minorParticipantAssentStatement: defaultEmptyString(translationInput.minorParticipantAssentStatement),
                witnessTitle: defaultEmptyString(translationInput.witnessTitle),
                witnessInstructions: defaultEmptyString(translationInput.witnessInstructions),
                witnessSignatureTitle: defaultEmptyString(translationInput.witnessSignatureTitle),
                witnessConfirmation: defaultEmptyString(translationInput.witnessConfirmation),
                providerTitle: defaultEmptyString(translationInput.providerTitle),
                providerInstructions: defaultEmptyString(translationInput.providerInstructions),
                providerSignatureTitle: defaultEmptyString(translationInput.providerSignatureTitle),
                providerConfirmation: defaultEmptyString(translationInput.providerConfirmation),
                patientFriendlyTitle: defaultEmptyString(translationInput.patientFriendlyTitle),
                patientFriendlySummary: defaultEmptyString(translationInput.patientFriendlySummary),
                informedConsentIRBApprovalStamp: defaultEmptyString(translationInput.informedConsentIRBApprovalStamp),
                risksOfEnrollment: defaultEmptyString(translationInput.risksOfEnrollment),
                benefitsOfEnrollment: defaultEmptyString(translationInput.benefitsOfEnrollment),
                leavingStudyEarlyText: defaultEmptyString(translationInput.leavingStudyEarlyText),
                injuryProtocolText: defaultEmptyString(translationInput.injuryProtocolText),
                participationTerminationText: defaultEmptyString(translationInput.participationTerminationText),
                futureUseOfDataText: defaultEmptyString(translationInput.futureUseOfDataText),
                confidentialityOfDataText: defaultEmptyString(translationInput.confidentialityOfDataText),
                compensationText: defaultEmptyString(translationInput.compensationText),
                costOfParticipationText: defaultEmptyString(translationInput.costOfParticipationText),
                sharingResultsText: defaultEmptyString(translationInput.sharingResultsText),
                alternativesToParticipation: defaultEmptyString(translationInput.alternativesToParticipation),
                hipaaAuthorizationText: defaultEmptyString(translationInput.hipaaAuthorizationText),
                proceduresAndInterventionsText: defaultEmptyString(translationInput.proceduresAndInterventionsText),
                purposeOfResearchText: defaultEmptyString(translationInput.purposeOfResearchText),
                informedConsentPrimaryDiseaseOrConditions: formatStringArray(deepClone(translationInput.informedConsentPrimaryDiseaseOrConditions), 1),
                timelineVisits: deepClone(translationInput.timelineVisits),
                additionalSections: deepClone(translationInput.additionalSections),
                enrollmentVideo: translationInput.enrollmentVideo,
                purposeOfDataBriefEducation: defaultEmptyString(translationInput.purposeOfDataBriefEducation),
                purposeOfDataTitle: defaultEmptyString(translationInput.purposeOfDataTitle),
                contactDetails: defaultEmptyString(translationInput.contactDetails),
            }),
        };
        return result;
    }
    get autoSaveStudyInputKeys() {
        return Object.keys(this.autoSaveStudyInput);
    }
    updateManageTranslationFormInput(studyTranslatableFields) {
        this.manageTranslationFormModel.input = {
            ...this.manageTranslationFormModel.input,
            studyTranslatableFields: studyTranslatableFields !== null && studyTranslatableFields !== void 0 ? studyTranslatableFields : [],
        };
    }
    get showRequirementsChangedBanner() {
        var _a;
        if (this.study === null || this.study === NOT_LOADED)
            return false;
        if (!this.study.requirementsUpdatedAt)
            return false;
        if (!((_a = this.selectedLanguageSectionTranslationProgressInfo) === null || _a === void 0 ? void 0 : _a.mostRecentTranslationUpdatedAt))
            return false;
        if (this.addProjectUIManager.didCloseTranslationsRequirementsChangedBanner)
            return false;
        return isBefore(new Date(this.selectedLanguageSectionTranslationProgressInfo.mostRecentTranslationUpdatedAt), new Date(this.study.requirementsUpdatedAt));
    }
    get selectedTranslationLanguage() {
        return this.addProjectUIManager.selectedTranslationLanguage;
    }
    get selectedTranslationFilter() {
        return this.addProjectUIManager.selectedTranslationFilter;
    }
    updateSelectedTranslationFilter(filter) {
        this.addProjectUIManager.updateSelectedTranslationFilter(filter);
    }
    get sectionsToShow() {
        var _a, _b, _c, _d;
        if (this.selectedTranslationFilter === "INCOMPLETE_SECTIONS") {
            return (_b = (_a = this.selectedLanguageSectionTranslationProgressInfo) === null || _a === void 0 ? void 0 : _a.incompleteSections) !== null && _b !== void 0 ? _b : [];
        }
        return (_d = (_c = this.selectedLanguageSectionTranslationProgressInfo) === null || _c === void 0 ? void 0 : _c.allAvailableSections) !== null && _d !== void 0 ? _d : [];
    }
    get languageSearchValues() {
        var _a;
        if (this.study === null || this.study === NOT_LOADED)
            return [];
        const values = [];
        (_a = this.study.studyTranslatableFields) === null || _a === void 0 ? void 0 : _a.forEach((fields) => {
            if (fields.language !== StudyTranslationLanguage.en && typeof fields.enabled === "boolean") {
                values.push(fields.language);
            }
        });
        values.push("COMING_SOON");
        return values;
    }
    updateSelectedTranslationLanguage(language) {
        if (language === "COMING_SOON")
            return;
        this.addProjectUIManager.updateSelectedTranslationLanguage(language);
    }
    get isPatientExperienceCompleted() {
        return this.addProjectUIManager.isPatientExperienceCompleted;
    }
    get showManageTranslationsButton() {
        return this.translationsExist;
    }
    get translationsExist() {
        return this.addProjectUIManager.allLanguageSectionTranslationProgressInfo.some((info) => info.enabled !== undefined);
    }
    get selectedLanguageSectionTranslationProgressInfo() {
        return this.addProjectUIManager.selectedLanguageSectionTranslationProgressInfo;
    }
    get selectedLanguageEnabled() {
        var _a;
        return !!((_a = this.addProjectUIManager.selectedLanguageSectionTranslationProgressInfo) === null || _a === void 0 ? void 0 : _a.enabled);
    }
    get translationsDialogTitleInfo() {
        return this.informedConsentTranslationsWizardUIManager.dialogTitleInfo;
    }
    get importTranslationDialogVisible() {
        return this.informedConsentTranslationsWizardUIManager.importTranslationDialogVisible;
    }
    get allTranslationLanguages() {
        if (this.study === null || this.study === NOT_LOADED)
            return [];
        return this.manageTranslationFormModel.input.studyTranslatableFields
            .filter((fields) => fields.language !== StudyTranslationLanguage.en)
            .map((fields) => ({ language: fields.language, enabled: fields.enabled }))
            .sort();
    }
    get cancelTranslationsText() {
        const currentPage = this.informedConsentTranslationsWizardUIManager.currentPage;
        const manualCompletionMessage = "If you leave this page, you can continue to translate the patient experience manually.";
        if (!currentPage)
            return manualCompletionMessage;
        switch (currentPage) {
            case "LANGUAGE_SELECTION":
                return null;
            case "IMPORT_METHOD":
                return `You have not chosen how you'd like to translate the patient experience. ${manualCompletionMessage}`;
            case "IMPORT_METHOD_AI_GENERATION":
                return `We aren't done generating translations. ${manualCompletionMessage}`;
            case "IMPORT_METHOD_AI_REGENERATION":
                return "We aren't done re-generating translations. If you leave this page, we'll keep the current translations that has already been entered.";
            case "IMPORT_METHOD_DOCUMENT_TRANSLATIONS":
                return `We aren't done importing details from your translation template. ${manualCompletionMessage}`;
            case "IMPORT_METHOD_DOCUMENT_TRANSLATIONS_GENERATING":
                return `You have not uploaded a translation template. ${manualCompletionMessage}`;
        }
        return null;
    }
    get confirmRetranslationDialogInfo() {
        return {
            title: "Re-translating with AI will overwrite this section",
            description: "Your current translation for this section will be replaced. This action cannot be undone.",
        };
    }
    get showCompletedTranslationsZeroState() {
        return this.selectedTranslationFilter === "INCOMPLETE_SECTIONS" && this.sectionsToShow.length === 0;
    }
    get showImagesAndVideosCopiedBanner() {
        var _a, _b;
        if (this.study === null || this.study === NOT_LOADED)
            return false;
        return ((_b = (_a = this.selectedLanguageSectionTranslationProgressInfo) === null || _a === void 0 ? void 0 : _a.copiedSections.length) !== null && _b !== void 0 ? _b : 0) > 0;
    }
    async autoSaveSelectedLanguage(input) {
        this.autoSaveStudyInput = input;
        if (this.selectedTranslationLanguage &&
            this.selectedTranslationLanguage !== "COMING_SOON" &&
            this.selectedTranslationLanguage === StudyTranslationLanguage.es) {
            if (input.enrollmentVideo instanceof File) {
                await this.uploadLanguageSpecificVideo(input.enrollmentVideo, "enrollment", this.selectedTranslationLanguage);
            }
        }
        const studyTranslatableFields = this.formatFormInputForUpdate(input);
        const didUpdateStudyTranslatableFields = Object.keys(studyTranslatableFields).length > 0;
        if (didUpdateStudyTranslatableFields) {
            this.addProjectUIManager.studyFormModel.updateInput({
                studyTranslatableFields: {
                    ...this.addProjectUIManager.studyFormModel.input.studyTranslatableFields,
                    ...studyTranslatableFields,
                },
            }, false);
        }
        await this.addProjectUIManager.autoSaveStudy({
            ...(didUpdateStudyTranslatableFields ? { studyTranslatableFields } : {}),
            additionalSections: input.additionalSections
                ? [...input.additionalSections, ...(this.formModel.input.additionalSections ? this.formModel.input.additionalSections : [])]
                : undefined,
        });
    }
    async uploadLanguageSpecificVideo(videoFile, videoType, language) {
        var _a;
        if (!this.studyId || !this.authManager.orgId)
            return;
        try {
            if (videoType === "enrollment") {
                this.enrollmentVideoTranslationUploadingInfo = { progress: 0 };
            }
            let englishVideoId = null;
            if (this.study && this.study !== NOT_LOADED) {
                if (videoType === "enrollment") {
                    const enrollmentVideo = this.study.enrollmentVideo;
                    englishVideoId = enrollmentVideo && !(enrollmentVideo instanceof File) ? enrollmentVideo.id : null;
                }
            }
            const uploadUrlMethod = videoType === "enrollment"
                ? this.studyService.getEnrollmentVideoUploadUrl.bind(this.studyService)
                : willNeverHappen(videoType);
            const fileUploadInfo = await uploadUrlMethod(this.authManager.orgId, this.studyId, new TranscodeInput({
                filename: videoFile.name,
                baseTranscode: { id: englishVideoId },
                language,
            }));
            if (isErr(fileUploadInfo)) {
                if (fileUploadInfo.error && typeof fileUploadInfo.error === "object" && "conflict" in fileUploadInfo.error) {
                    const conflictError = fileUploadInfo.error;
                    if ((_a = conflictError.conflict) === null || _a === void 0 ? void 0 : _a.id) {
                        console.log(`Using existing ${videoType} video transcode for language ${language}:`, conflictError.conflict.id);
                        if (videoType === "enrollment") {
                            this.enrollmentVideoTranslationUploadingInfo = null;
                        }
                        return;
                    }
                }
                if (videoType === "enrollment") {
                    this.enrollmentVideoTranslationUploadingInfo = null;
                }
                return;
            }
            if (videoType === "enrollment" && this.enrollmentVideoTranslationUploadingInfo) {
                this.enrollmentVideoTranslationUploadingInfo.signedUrl = fileUploadInfo.signedUrl;
            }
            await this.provisioningService.uploadFileToUrl(fileUploadInfo.signedUrl, videoFile, (progressPercentage) => {
                if (videoType === "enrollment" && this.enrollmentVideoTranslationUploadingInfo) {
                    this.enrollmentVideoTranslationUploadingInfo.progress = progressPercentage;
                }
            });
            if (videoType === "enrollment") {
                this.enrollmentVideoTranslationUploadingInfo = null;
            }
        }
        catch (error) {
            console.error(`Error uploading ${videoType} video for language ${language}:`, error);
            if (videoType === "enrollment") {
                this.enrollmentVideoTranslationUploadingInfo = null;
            }
        }
    }
    async autoSaveLanguages(input) {
        if (this.study === null || this.study === NOT_LOADED || !this.authManager.orgId || !input.studyTranslatableFields)
            return;
        await this.studyService.updateStudyVersion(this.study.id, this.authManager.orgId, {
            studyTranslatableFields: [...input.studyTranslatableFields],
        });
        await this.addProjectUIManager.refreshStudy();
    }
    formatFormInputForUpdate(input) {
        const result = {};
        Object.keys(input).forEach((key) => {
            if (isKeyOfObject(key, input)) {
                const value = input[key];
                if (key === "additionalSections" || key === "sectionImages") {
                }
                else if (key === "timelineVisits") {
                    if (value instanceof Array) {
                        result.timelineVisits = value.map((item) => typeof item === "object" ? item : { period: "", description: "" });
                    }
                }
                else if (key === "informedConsentPrimaryDiseaseOrConditions") {
                    if (value instanceof Array) {
                        result.informedConsentPrimaryDiseaseOrConditions = value.map((item) => (typeof item === "string" ? item : ""));
                    }
                }
                else if (key === "enrollmentVideo") {
                }
                else if (key === "deleteEnrollmentTranscode") {
                    if (typeof value === "boolean") {
                        ;
                        result[key] = value;
                    }
                }
                else if (typeof value === "string") {
                    result[key] = this.nullIfEmpty(value);
                }
            }
        });
        return result;
    }
    downloadEnglishTranslationsCSV() {
        var _a, _b;
        if (this.study === null || this.study === NOT_LOADED || !this.addProjectUIManager.projectPages)
            return;
        const availableTranslatableFields = getTranslatableFieldsFromProjectSections((_b = (_a = this.addProjectUIManager.projectPages.pages.find((page) => page.pageName === ProjectPageName.PATIENT_EXPERIENCE)) === null || _a === void 0 ? void 0 : _a.sections) !== null && _b !== void 0 ? _b : []);
        downloadEnglishInformedConsentTranslationsAsCSV(this.study, availableTranslatableFields);
    }
    goToTranslationsHelpPage() {
        window.open("https://www.healthex.io/", "_blank");
    }
    downloadEnglishTranslations() {
        if (this.study === null || this.study === NOT_LOADED)
            return;
        this.downloadEnglishTranslationsCSV();
        this.persistence.setEnglishStringsDownloaded(this.study.id);
        this.addProjectUIManager.didDownloadTranslationsThisSession = true;
    }
    downloadEnglishTranslationsAndDisplayDialog() {
        this.downloadEnglishTranslations();
        this.showDownloadDialogImportButton = true;
        this.showDownloadDialogBackButton = false;
        this.downloadCompleteDialog.open = true;
    }
    async saveAndExit() {
        this.downloadCompleteDialog.open = false;
        this.downloadCompleteDialog.open = false;
        await this.addProjectUIManager.saveAndExit();
    }
    openAddLanguageDialog() {
        this.translationWizardImportType = "FULL";
        this.translationWizardImportLanguage = undefined;
        this.informedConsentTranslationsWizardUIManager.importTranslationDialogVisible = true;
    }
    openImportTranslationsDocumentDialog(language) {
        this.translationWizardImportType = "DOCUMENT_IMPORT";
        this.translationWizardImportLanguage = language;
        this.informedConsentTranslationsWizardUIManager.importTranslationDialogVisible = true;
        this.downloadCompleteDialog.open = false;
        this.welcomeBackToTranslationsDialog.open = false;
    }
    openImportAITranslationsDialog(language) {
        this.translationWizardImportType = "AI_IMPORT";
        this.translationWizardImportLanguage = language;
        this.informedConsentTranslationsWizardUIManager.importTranslationDialogVisible = true;
    }
    openManageTranslationsDialog() {
        this.manageTranslationsDialog.open = true;
    }
    async closeTranslationImportDialog() {
        if (!this.cancelTranslationsText) {
            this.translationsConfirmCloseDialog.open = false;
            this.informedConsentTranslationsWizardUIManager.importTranslationDialogVisible = false;
            return;
        }
        const confirmed = await this.translationsConfirmCloseDialog.showAndWait();
        this.translationsConfirmCloseDialog.open = false;
        if (confirmed) {
            this.informedConsentTranslationsWizardUIManager.importTranslationDialogVisible = false;
        }
    }
    async updateStudyForTranslations(studyTranslatableFields, outOfDateImportedTranslations, isSectionUpdate) {
        this.autoSaveStudyInput = {};
        await this.addProjectUIManager.updateStudyWithTranslationImport(studyTranslatableFields, outOfDateImportedTranslations, isSectionUpdate);
    }
    updateManageTranslationEnabled(info) {
        this.manageTranslationFormModel.updateInput({
            studyTranslatableFields: this.manageTranslationFormModel.input.studyTranslatableFields.map((field) => field.language === info.language ? { ...field, enabled: info.enabled } : field),
        });
    }
    enableSelectedLanguage() {
        this.manageTranslationFormModel.updateInput({
            studyTranslatableFields: this.manageTranslationFormModel.input.studyTranslatableFields.map((field) => field.language === this.selectedTranslationLanguage ? { ...field, enabled: true } : field),
        });
    }
    getSectionTranslationProgressInfo(language) {
        var _a, _b;
        return (_b = (_a = this.addProjectUIManager.allLanguageSectionTranslationProgressInfo.find((info) => info.language === language)) === null || _a === void 0 ? void 0 : _a.progress) !== null && _b !== void 0 ? _b : 0;
    }
    getTranslationTooltip(language) {
        return this.getSectionTranslationProgressInfo(language) > 0 ? "Re-translate with AI" : "Translate with AI";
    }
    manageTranslationsDownloadEnglishCSV() {
        if (this.study === null || this.study === NOT_LOADED)
            return;
        this.downloadEnglishTranslationsCSV();
        this.persistence.setEnglishStringsDownloaded(this.study.id);
        this.addProjectUIManager.didDownloadTranslationsThisSession = true;
        this.showDownloadDialogImportButton = false;
        this.showDownloadDialogBackButton = false;
        this.downloadCompleteDialog.open = true;
    }
    onBackDownloadCompleteDialog() {
        this.downloadCompleteDialog.open = false;
        this.openImportTranslationsDocumentDialog();
    }
    onDialogEnglishTranslationsDownloaded() {
        this.showDownloadDialogBackButton = true;
        this.showDownloadDialogImportButton = false;
        this.downloadCompleteDialog.open = true;
    }
    getSectionInfo(section) {
        var _a, _b, _c, _d, _e, _f;
        return {
            title: projectSectionTitle[section],
            incomplete: !!((_a = this.selectedLanguageSectionTranslationProgressInfo) === null || _a === void 0 ? void 0 : _a.incompleteSections.includes(section)),
            outdated: !!((_b = this.selectedLanguageSectionTranslationProgressInfo) === null || _b === void 0 ? void 0 : _b.outdatedSections.includes(section)),
            hasTranslations: !!((_c = this.selectedLanguageSectionTranslationProgressInfo) === null || _c === void 0 ? void 0 : _c.hasTranslationsSections.includes(section)),
            copied: !!((_d = this.selectedLanguageSectionTranslationProgressInfo) === null || _d === void 0 ? void 0 : _d.copiedSections.includes(section)),
            generatingAI: this.sectionsGeneratingAI.has(section),
            generatingAIError: this.sectionsGeneratingAIErrors.has(section),
            languageName: languageDescription(this.addProjectUIManager.selectedTranslationLanguage),
            manuallyEdited: !!((_e = this.selectedLanguageSectionTranslationProgressInfo) === null || _e === void 0 ? void 0 : _e.manuallyEditedSections.includes(section)),
            showGeneratedWithAI: !!((_f = this.selectedLanguageSectionTranslationProgressInfo) === null || _f === void 0 ? void 0 : _f.generatedWithAISections.includes(section)),
        };
    }
    getSectionRequired(section) {
        var _a;
        return !!((_a = this.selectedLanguageSectionTranslationProgressInfo) === null || _a === void 0 ? void 0 : _a.incompleteSections.includes(section));
    }
    async translateWithAI(section, isRetranslation) {
        var _a, _b, _c, _d, _e;
        if (this.study === null || this.study === NOT_LOADED)
            return;
        if (!this.selectedLanguageSectionTranslationProgressInfo)
            return;
        const sectionInfo = this.selectedLanguageSectionTranslationProgressInfo.sectionLanguageInfo[section];
        if (!(sectionInfo === null || sectionInfo === void 0 ? void 0 : sectionInfo.english))
            return;
        if (this.sectionsGeneratingAI.has(section))
            return;
        this.sectionsGeneratingAI.add(section);
        if (isRetranslation) {
            const confirmed = await this.confirmRetranslationDialog.showAndWait();
            if (!confirmed) {
                this.sectionsGeneratingAI.delete(section);
                return;
            }
        }
        const { enabled: englishEnabled, importType: englishImportType, language: englishLanguage, additionalSection, additionalQuestions, sectionImages, ...englishStudyTranslatableFields } = sectionInfo.english;
        let error = false;
        try {
            const additionalSectionObject = additionalSection !== undefined ? (_a = JSON.parse(JSON.stringify(additionalSection))) !== null && _a !== void 0 ? _a : null : null;
            const additionalQuestionsObject = additionalQuestions !== undefined ? (_b = JSON.parse(JSON.stringify(additionalQuestions))) !== null && _b !== void 0 ? _b : null : null;
            const studySectionImagesObject = sectionImages !== undefined ? (_c = JSON.parse(JSON.stringify(sectionImages))) !== null && _c !== void 0 ? _c : null : null;
            const additionalSectionsToSave = [];
            if (additionalSectionObject) {
                additionalSectionsToSave.push(additionalSectionObject);
            }
            if (additionalQuestionsObject) {
                additionalSectionsToSave.push(...additionalQuestionsObject);
            }
            const aiTranslationResponse = await this.studyManager.translateStudyFields(this.study.id, {
                language: this.addProjectUIManager.selectedTranslationLanguage,
                studyTranslatableFields: englishStudyTranslatableFields,
                ...(additionalSectionsToSave.length > 0 ? { additionalSections: additionalSectionsToSave } : []),
                ...(studySectionImagesObject && studySectionImagesObject.length > 0
                    ? { studySectionImages: studySectionImagesObject }
                    : []),
            });
            if (!aiTranslationResponse) {
                this.sectionsGeneratingAI.delete(section);
                this.sectionsGeneratingAIErrors.add(section);
                return;
            }
            const sectionImagesToSave = [];
            if (studySectionImagesObject) {
                (_d = aiTranslationResponse.studySectionImages) === null || _d === void 0 ? void 0 : _d.forEach((newImage) => {
                    var _a, _b, _c, _d, _e;
                    const currentTranslatedImage = (_b = (_a = this.selectedLanguageSectionTranslationProgressInfo) === null || _a === void 0 ? void 0 : _a.translationInput.sectionImages) === null || _b === void 0 ? void 0 : _b.find((currentImage) => currentImage.translatedFrom === newImage.translatedFrom);
                    const currentEnglishImage = studySectionImagesObject.find((currentImage) => currentImage.id === newImage.translatedFrom);
                    if (currentEnglishImage) {
                        sectionImagesToSave.push({
                            sectionName: currentEnglishImage.sectionName,
                            caption: (_c = newImage.caption) !== null && _c !== void 0 ? _c : "",
                            imageId: currentTranslatedImage === null || currentTranslatedImage === void 0 ? void 0 : currentTranslatedImage.id,
                            additionalSectionId: (_d = currentTranslatedImage === null || currentTranslatedImage === void 0 ? void 0 : currentTranslatedImage.additionalSectionId) !== null && _d !== void 0 ? _d : currentEnglishImage === null || currentEnglishImage === void 0 ? void 0 : currentEnglishImage.additionalSectionId,
                            translatedFrom: currentEnglishImage.id,
                            order: (_e = currentTranslatedImage === null || currentTranslatedImage === void 0 ? void 0 : currentTranslatedImage.order) !== null && _e !== void 0 ? _e : 0,
                        });
                    }
                });
            }
            const language = this.addProjectUIManager.selectedTranslationLanguage;
            const additionalSectionsTranslated = aiTranslationResponse.additionalSections
                ? (_e = aiTranslationResponse.additionalSections.map((x) => {
                    var _a, _b, _c, _d;
                    const currentTranslatedAdditionalSection = (_b = (_a = this.studyTranslationFormModel.input.additionalSections) === null || _a === void 0 ? void 0 : _a.find((s) => x.translatedFrom === s.translatedFrom)) !== null && _b !== void 0 ? _b : null;
                    return {
                        ...currentTranslatedAdditionalSection,
                        included: (_c = currentTranslatedAdditionalSection === null || currentTranslatedAdditionalSection === void 0 ? void 0 : currentTranslatedAdditionalSection.included) !== null && _c !== void 0 ? _c : true,
                        language: (_d = currentTranslatedAdditionalSection === null || currentTranslatedAdditionalSection === void 0 ? void 0 : currentTranslatedAdditionalSection.language) !== null && _d !== void 0 ? _d : language,
                        ...x,
                    };
                })) !== null && _e !== void 0 ? _e : []
                : [];
            await this.updateStudyForTranslations({
                translatableFields: {
                    ...aiTranslationResponse.studyTranslatableFields,
                    language,
                    section,
                },
                ...(additionalSectionsTranslated.length > 0
                    ? {
                        additionalSections: [...additionalSectionsTranslated],
                    }
                    : undefined),
                ...(sectionImagesToSave.length > 0 ? { sectionImages: sectionImagesToSave } : undefined),
                language,
            }, {}, true);
        }
        catch (e) {
            error = true;
            this.sectionsGeneratingAIErrors.add(section);
        }
        finally {
            if (!error)
                this.sectionsGeneratingAIErrors.delete(section);
            this.sectionsGeneratingAI.delete(section);
        }
    }
    async editEnglishTranslations(sectionName) {
        const confirmed = await this.editEnglishTranslationsDialog.showAndWait();
        if (!confirmed)
            return;
        await this.addProjectUIManager.goToPage(ProjectPageName.PATIENT_EXPERIENCE, sectionName);
    }
    getTranslatableFieldInfo(sectionName, fieldName, input) {
        var _a;
        const section = (_a = this.selectedLanguageSectionTranslationProgressInfo) === null || _a === void 0 ? void 0 : _a.sectionLookup[sectionName];
        if (!section)
            return { title: "", placeholder: "", multiline: false, minRows: 1 };
        const field = section.fields.find((f) => { var _a; return (_a = f.translatableFieldNames) === null || _a === void 0 ? void 0 : _a.includes(fieldName); });
        if (!field)
            return { title: "", placeholder: "", multiline: false, minRows: 1 };
        const { label, translationLabel, useTranslationLabelFieldName, placeholder, multiline, minRows } = field;
        const useTranslationLabelForTitle = translationLabel &&
            useTranslationLabelFieldName &&
            isKeyOfObject(useTranslationLabelFieldName, input) &&
            input[useTranslationLabelFieldName] === true;
        return {
            title: useTranslationLabelForTitle ? translationLabel : label,
            placeholder,
            multiline,
            minRows,
        };
    }
    initiateEnrollmentVideoUploadingProgress() {
        var _a, _b;
        (_b = (_a = this.addProjectUIManager).initiateEnrollmentVideoUploadingProgress) === null || _b === void 0 ? void 0 : _b.call(_a);
    }
    initiateEnrollmentVideoTranslationUploadingProgress() {
        this.enrollmentVideoTranslationUploadingInfo = { progress: 0 };
    }
    get enrollmentVideoUploadingInfo() {
        return this.addProjectUIManager.enrollmentVideoUploadingInfo;
    }
    async deleteEnrollmentVideo() {
        await this.deleteVideo("enrollment");
    }
    async deleteVideo(videoType) {
        if (!this.selectedTranslationLanguage || this.selectedTranslationLanguage === "COMING_SOON") {
            console.warn("Cannot delete video for invalid language");
            return;
        }
        if (!this.studyId || !this.authManager.orgId) {
            console.error("Missing studyId or orgId for video deletion");
            return;
        }
        try {
            if (videoType === "enrollment") {
                const updateInput = {
                    enrollmentVideo: null,
                    deleteEnrollmentTranscode: true,
                };
                this.studyTranslationFormModel.updateInput(updateInput);
            }
            console.log(`Successfully deleted ${videoType} video for language ${this.selectedTranslationLanguage}`);
        }
        catch (error) {
            console.error(`Failed to delete ${videoType} video:`, error);
        }
    }
    async ensureTranslationImagesExist(language) {
        var _a, _b, _c, _d, _e, _f;
        if (!this.authManager.orgId || !this.studyId || language === StudyTranslationLanguage.en) {
            return;
        }
        if (this.creatingTranslationImages)
            return;
        this.creatingTranslationImages = true;
        try {
            const sectionsToProcess = this.sectionsToShow.filter((section) => {
                const sectionInfo = this.getSectionInfo(section);
                return sectionInfo.incomplete || sectionInfo.copied;
            });
            for (const section of sectionsToProcess) {
                const sectionName = this.getImageSectionNameForSection(section);
                if (!sectionName)
                    continue;
                let additionalSectionId;
                if (sectionName.startsWith("ADDITIONAL_INFORMATION")) {
                    additionalSectionId =
                        (_d = (_c = (_b = (_a = this.addProjectUIManager.projectPages) === null || _a === void 0 ? void 0 : _a.pages.find((page) => page.pageName === ProjectPageName.PATIENT_EXPERIENCE)) === null || _b === void 0 ? void 0 : _b.sections.find((section) => section.sectionName === sectionName)) === null || _c === void 0 ? void 0 : _c.objId) !== null && _d !== void 0 ? _d : undefined;
                }
                const imageSectionName = sectionName.startsWith("ADDITIONAL_INFORMATION")
                    ? ProjectSectionName.ADDITIONAL_INFORMATION
                    : sectionName;
                const englishImages = (_e = this.getSectionImages(imageSectionName, StudyTranslationLanguage.en, additionalSectionId)) !== null && _e !== void 0 ? _e : [];
                if (englishImages.length === 0)
                    continue;
                const translationImages = (_f = this.getSectionImages(imageSectionName, language, additionalSectionId)) !== null && _f !== void 0 ? _f : [];
                const missingTranslationImages = englishImages.filter((englishImage) => !translationImages.some((translationImage) => translationImage.order === englishImage.order));
                if (missingTranslationImages.length === 0)
                    continue;
                const createSectionImages = missingTranslationImages.map((englishImage) => ({
                    imageUrl: "",
                    caption: "",
                    sectionName: imageSectionName,
                    language,
                    status: StudySectionImageStatus.ACTIVE,
                    order: englishImage.order,
                    sectionType: StudySectionType.REGULAR,
                    additionalSectionId: englishImage.additionalSectionId,
                    translatedFrom: englishImage.id,
                }));
                await this.studySectionImageService.createStudySectionImageBatch(this.authManager.orgId, this.studyId, {
                    images: createSectionImages,
                });
                await this.refreshStudy();
            }
        }
        catch (error) {
            console.error("Failed to create translation image records:", error);
        }
        finally {
            this.creatingTranslationImages = false;
        }
    }
    getImageSectionNameForSection(sectionName) {
        return sectionName;
    }
    closeTranslationsAddedDialog() {
        this.addProjectUIManager.translationsManuallyCompleted = false;
        this.translationsAddedDialog.open = false;
    }
    async continueToPreview() {
        this.closeTranslationsAddedDialog();
        await this.addProjectUIManager.goToPage(ProjectStaticPageName.PREVIEW);
    }
    downloadEnglishAndAllTranslations() {
        if (this.study === null || this.study === NOT_LOADED)
            return;
        this.closeTranslationsAddedDialog();
        this.downloadEnglishTranslationsCSV();
    }
    closeTranslationsRequirementsChangedBanner() {
        this.addProjectUIManager.didCloseTranslationsRequirementsChangedBanner = true;
    }
}
__decorate([
    observable
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "enrollmentVideoTranslationUploadingInfo", void 0);
__decorate([
    observable
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "translationWizardImportType", void 0);
__decorate([
    observable
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "translationWizardImportLanguage", void 0);
__decorate([
    observable
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "showDownloadDialogBackButton", void 0);
__decorate([
    observable
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "showDownloadDialogImportButton", void 0);
__decorate([
    observable
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "sectionsGeneratingAI", void 0);
__decorate([
    observable
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "sectionsGeneratingAIErrors", void 0);
__decorate([
    computed
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "showRequirementsChangedBanner", null);
__decorate([
    computed
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "selectedTranslationLanguage", null);
__decorate([
    computed
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "selectedTranslationFilter", null);
__decorate([
    computed
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "sectionsToShow", null);
__decorate([
    computed
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "languageSearchValues", null);
__decorate([
    computed
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "isPatientExperienceCompleted", null);
__decorate([
    computed
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "showManageTranslationsButton", null);
__decorate([
    computed
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "translationsExist", null);
__decorate([
    computed
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "selectedLanguageSectionTranslationProgressInfo", null);
__decorate([
    computed
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "selectedLanguageEnabled", null);
__decorate([
    computed
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "translationsDialogTitleInfo", null);
__decorate([
    computed
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "importTranslationDialogVisible", null);
__decorate([
    computed
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "allTranslationLanguages", null);
__decorate([
    computed
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "cancelTranslationsText", null);
__decorate([
    computed
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "confirmRetranslationDialogInfo", null);
__decorate([
    computed
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "showCompletedTranslationsZeroState", null);
__decorate([
    computed
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "showImagesAndVideosCopiedBanner", null);
__decorate([
    computed
], AddJoinCommonInformedConsentTranslationsViewModel.prototype, "enrollmentVideoUploadingInfo", null);
